/** Preamble prepended to a research-mode message's wire text so the agent
 *  activates the research-intake skill. Displayed text stays clean. */
export const RESEARCH_INTENT_PREAMBLE =
  "[Research mode] The user wants to run a deep-research / literature task. " +
  "Activate the research-intake skill and run the intake before creating the session.";

/** Build the text sent to the agent for a research-mode turn (UI shows the raw content). */
export function researchWireContent(content: string): string {
  return `${RESEARCH_INTENT_PREAMBLE}\n\n${content}`;
}

/** Return the session_id from a research_session_started extras item, or null. */
export function findResearchSessionId(
  extras: ReadonlyArray<{ action?: string; session_id?: string }> | null | undefined
): string | null {
  const item = extras?.find(e => e.action === "research_session_started");
  return item?.session_id ?? null;
}

export interface ResearchAttachmentRecord {
  attachment_id: string;
  filename: string;
  format: string;
  size: number;
}

interface UploadOptions {
  /** Inject a fetch-compatible function for testing.
   *  In production, omit this and the shared fetchClient is used so Auth0 JWT
   *  and X-TEAM-ID headers are attached automatically via its middleware chain. */
  fetchImpl?: typeof fetch;
}

/** Upload one document to the research-attachment endpoint and return its record.
 *  Bytes go out-of-band here; only the returned id travels through the agent. */
export async function uploadResearchAttachment(
  file: File,
  options: UploadOptions = {}
): Promise<ResearchAttachmentRecord> {
  const form = new FormData();
  form.append("file", file);

  if (options.fetchImpl) {
    // Test path: caller injects a mock fetch so no network or auth setup is needed.
    const resp = await options.fetchImpl("/api/research_tasks/research_attachments/", {
      method: "POST",
      body: form
    });
    if (!resp.ok) {
      throw new Error(`Attachment upload failed (${resp.status})`);
    }
    return (await resp.json()) as ResearchAttachmentRecord;
  }

  // Production path: dynamically import fetchClient so this module stays importable in node
  // test environments (fetchClient depends on import.meta.env at module initialisation time).
  const { fetchClient } = await import("@app/config/api/api");
  const r = await fetchClient.POST("/research-tasks/research_attachments/", {
    body: form,
    // Bypass openapi-fetch's default JSON serialiser; let the browser set the
    // multipart/form-data boundary automatically.
    bodySerializer: b => b
  });

  if (r.error) {
    throw new Error(`Attachment upload failed: ${JSON.stringify(r.error)}`);
  }
  return r.data;
}

/** Build the answer text sent back to the agent after an upload step. The id
 *  block is delimited so the research-intake skill copies the ids verbatim into
 *  research_sessions_create(attachment_ids=...). */
export function formatAttachmentAnswer(records: readonly ResearchAttachmentRecord[]): string {
  if (records.length === 0) {
    return "I'm attaching no documents.";
  }
  const names = records.map(r => r.filename).join(", ");
  const ids = records.map(r => r.attachment_id).join(", ");
  return `I've attached: ${names}.\nattachment_ids: ${ids}`;
}
